import * as Sentry from '@sentry/vue'
import { productRelease } from '@zerohuman/shared'
import type { App } from 'vue'
import type { Router } from 'vue-router'
import { type RequestFailure, requestFailure } from '@/api'
import { isPublicScreen } from '@/lib/publicScreens'

type SentryConfig = { dsn: string; environment: string; release: string }

/**
 * A path segment that is itself a credential: the token of a Needs you email's decide link (#1778), in the page's
 * `/decide/<token>` and the API's `/gate-links/<token>`. Whoever holds one can approve or reject as the owner it was
 * mailed to, so it never leaves the browser (#2183).
 */
const DECIDE_TOKEN_IN_PATH = /(\/(?:decide|gate-links)\/)[^/?#\s"'\\]+/g

/**
 * `value` with every decide-link token replaced by `[token]`, in every string through plain objects and arrays: the
 * page URL, the transaction name, navigation and fetch breadcrumbs, span descriptions and data. Anything else is kept.
 */
export function scrubDecideTokens<T>(value: T, depth = 0): T {
    if (typeof value === 'string') {
        return value.replace(DECIDE_TOKEN_IN_PATH, '$1[token]') as T
    }
    if (depth > 12 || value === null || typeof value !== 'object') {
        return value
    }
    if (Array.isArray(value)) {
        return value.map(item => scrubDecideTokens(item, depth + 1)) as T
    }
    const prototype = Object.getPrototypeOf(value)
    if (prototype !== Object.prototype && prototype !== null) {
        return value
    }
    const out: Record<string, unknown> = {}
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
        out[key] = scrubDecideTokens(nested, depth + 1)
    }
    return out as T
}

type SentryLog = Parameters<NonNullable<NonNullable<Parameters<typeof Sentry.init>[0]>['beforeSendLog']>>[0]

/**
 * `log` with every decide-link token cut out of its message and its attributes. Console output goes to Sentry as logs
 * (`consoleLoggingIntegration`, every level), and a line can quote the page address or a gate-link request URL. The
 * message of a parameterised log is a `String` object, which `scrubDecideTokens` keeps as it is, so the message is
 * scrubbed as text; its template and parameters are already attributes by now, and are scrubbed with them.
 */
export function scrubDecideTokensFromLog(log: SentryLog): SentryLog {
    const message = String(log.message)
    const scrubbed = scrubDecideTokens(message)
    return {
        ...log,
        message: scrubbed === message ? log.message : (scrubbed as SentryLog['message']),
        ...(log.attributes ? { attributes: scrubDecideTokens(log.attributes) } : {}),
    }
}

/**
 * What Sentry starts with. Every error, transaction, breadcrumb and log is scrubbed of decide-link tokens on its way
 * out. A page loaded on a decide link records no session replay at all: a replay keeps the page's address in places
 * the hooks above do not reach, and that address is the credential (#2183). The decide page links out with full page
 * loads, so the next page boots without the token and records as usual.
 */
export function sentryInitOptions(
    app: App,
    router: Router,
    config: SentryConfig,
    pathname: string = window.location.pathname,
): Parameters<typeof Sentry.init>[0] {
    const replay = isPublicScreen(pathname) ? [] : [Sentry.replayIntegration()]
    return {
        app,
        dsn: config.dsn,
        environment: config.environment,
        release: config.release,
        enableLogs: true,
        tracesSampleRate: 1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1,
        tracePropagationTargets: [
            'localhost',
            '127.0.0.1',
            /^https:\/\/(portal|api)\.local\.zerohuman\.com/,
            /^https:\/\/(portal|api)\.zerohuman\.com/,
            /^https:\/\/[a-z0-9.-]+\.os\.zerohuman\.com/,
        ],
        dataCollection: {
            userInfo: false,
            cookies: false,
            httpBodies: [],
        },
        integrations: [
            Sentry.browserTracingIntegration({ router }),
            ...replay,
            Sentry.consoleLoggingIntegration({
                levels: ['debug', 'log', 'info', 'warn', 'error'],
            }),
        ],
        beforeSend: event => scrubDecideTokens(event),
        beforeSendTransaction: event => scrubDecideTokens(event),
        beforeBreadcrumb: breadcrumb => scrubDecideTokens(breadcrumb),
        beforeSendLog: log => scrubDecideTokensFromLog(log),
    }
}

/** Public ingest DSN for os-portal (zerohuman-vl). Override with VITE_SENTRY_DSN. */
export const OS_PORTAL_DSN =
    'https://96bf8b6842be3ad14a280568a6db1001@o4512101775376384.ingest.us.sentry.io/4512101961433088'

let booted = false

export function resetSentryForTests(): void {
    booted = false
}

export function sentryEnvironmentFromHost(hostname = window.location.hostname): string {
    if (hostname.startsWith('portal-pr-') && hostname.endsWith('.os.zerohuman.com')) {
        return 'preview'
    }
    if (hostname === 'portal.zerohuman.com') {
        return 'production'
    }
    return 'local'
}

function initSentry(app: App, router: Router, config: SentryConfig): void {
    if (booted || !config.dsn) {
        return
    }
    Sentry.init(sentryInitOptions(app, router, config))
    booted = true
}

/**
 * Production only. The DSN above is bundled into every build, so the served origin is what decides:
 * `portal.zerohuman.com` reports, a preview host and a laptop do not. `import.meta.env.PROD` is the
 * browser's `NODE_ENV=production` — it keeps a dev server with `VITE_SENTRY_ENVIRONMENT=production`
 * in its environment from reporting real user sessions.
 */
export function sentryReportingEnabled(environment: string, productionBuild = import.meta.env.PROD): boolean {
    return productionBuild && environment === 'production'
}

export async function bootSentry(app: App, router: Router): Promise<void> {
    const dsn = String(import.meta.env.VITE_SENTRY_DSN || OS_PORTAL_DSN)
    const environment = String(import.meta.env.VITE_SENTRY_ENVIRONMENT || sentryEnvironmentFromHost())
    if (!sentryReportingEnabled(environment)) {
        return
    }
    initSentry(app, router, { dsn, environment, release: sentryRelease() })
}

export function sentryRelease(): string {
    return String(import.meta.env.VITE_SENTRY_RELEASE || '') || productRelease()
}

/** Somebody who signed in without GitHub (#636) has no login: Sentry then knows them by id alone. */
export function setSentryUser(user: { id: string; githubLogin: string | null } | null): void {
    if (!booted) {
        return
    }
    Sentry.setUser(user ? { id: user.id, ...(user.githubLogin ? { username: user.githubLogin } : {}) } : null)
}

/** What kind of failure a non-request one was, without its message: `TypeError`, `SyntaxError`, or `string`. */
function failureKind(error: unknown): string {
    return error instanceof Error ? error.name : typeof error
}

/**
 * The shape of a route's own words (`runs`, `kpi-targets`) and of the API version (`v1`): the only path segments a
 * report keeps. A slug or a name made of the same lower-case letters and hyphens has the same shape, so it is kept
 * too (see `reportedPath`).
 */
const ROUTE_SEGMENT = /^(?:[a-z]+(?:-[a-z]+)*|v\d+)$/

/**
 * A request path as a report names it: every segment that is not route-shaped becomes `:id`, so `/v1/runs/0b6e…/retry`
 * and `/v1/runs/run-1/retry` are both `/v1/runs/:id/retry`. Any segment with a digit, an upper-case letter, a `_`, a
 * dot or a `%` is masked: a uuid (every one carries its version digit), a numeric id, a tool-call id (`toolu_…`), a
 * domain and an encoded value. Those stay in the browser, and one route is one tag value and one Sentry issue however
 * many of them fail on it.
 *
 * What it cannot mask is a value made only of lower-case letters and hyphens, such as a slug or a name: without a
 * route table that reads exactly like a route word, so it is kept. No path the portal requests carries one today:
 * each id the portal puts in a request path is one of the kinds above. The API routes that do take a slug or a name
 * (`/v1/public/org/:enterprise`, `/v1/public/metrics/:enterprise`, `/v1/tools/built-in/:name`) are not called from the
 * portal. A screen that starts reporting a failure on one of them has to mask that segment here first.
 */
function reportedPath(path: string): string {
    return path
        .split('/')
        .map(segment => (segment === '' || ROUTE_SEGMENT.test(segment) ? segment : ':id'))
        .join('/')
}

/**
 * Report a failure a screen handles (and shows) to Sentry: the block that failed, the route of the failed request
 * and its status, and nothing else (#1677). One call is one event.
 *
 * The caught error is never handed over as it is. A refusal's message is the server's own words, and a failure that
 * is not a refusal can quote the response too (a body that would not parse names its first characters). So the
 * event is a new error built from the block, the route and the status, or the failure's kind (`TypeError` for a
 * network error) when no request was refused. The route is the request's path with its query string already gone
 * and each uuid, numeric id, tool-call id, domain or encoded value in it replaced by `:id` (`reportedPath`). A slug
 * or a name in a path would be kept, and no path the portal requests carries one.
 *
 * It is called whether or not Sentry booted: before `bootSentry` (a preview, a laptop, a test) the SDK drops it.
 */
export function reportHandledError(error: unknown, block: string): void {
    const failure = requestFailure(error)
    const refused = failure ? { path: reportedPath(failure.path), status: String(failure.status) } : null
    const cause = refused ? `${refused.path} answered ${refused.status}` : failureKind(error)
    const report = new Error(`Could not load ${block}: ${cause}`)
    report.name = 'HandledLoadFailure'
    Sentry.captureException(report, {
        tags: {
            block,
            ...(refused ? { 'request.path': refused.path, 'request.status': refused.status } : {}),
        },
        // One issue per block and route, so a block that fails on every load does not hide another that fails once,
        // and a route that fails for many ids is still one issue.
        fingerprint: ['handled-load-failure', block, refused ? refused.path : cause],
    })
}

/**
 * Report a read the API refused with 403 (#1801, OS-PORTAL-5): the caller's grant does not cover the block. That is
 * the enterprise's permission model doing its job, as the API's own session guard treats it (#1678), not a failed
 * load. So it goes to Sentry as a warning, never an error, and under a fingerprint of its own: a member with a narrow
 * grant opening a screen raises no alert, a spike of refusals can still be seen, and a refusal never groups with a
 * real failure of the same block and route.
 *
 * It carries what `reportHandledError` carries and nothing more: the block, the route with its ids masked
 * (`reportedPath`) and the status. The refusal's message is the server's own words, so it is not sent.
 */
export function reportNoAccess(refusal: RequestFailure, block: string): void {
    const path = reportedPath(refusal.path)
    const status = String(refusal.status)
    Sentry.captureMessage(`No access to ${block}: ${path} answered ${status}`, {
        level: 'warning',
        tags: { block, 'request.path': path, 'request.status': status },
        fingerprint: ['handled-load-no-access', block, path],
    })
}
